import { Check } from 'lucide-react';

interface WorkflowStep {
  id: string;
  label: string;
  date?: string;
  status: 'completed' | 'current' | 'pending';
}

interface WorkflowTimelineProps {
  steps: WorkflowStep[];
  className?: string;
  compact?: boolean;
}

export default function WorkflowTimeline({
  steps,
  className = '',
  compact = false,
}: WorkflowTimelineProps) {
  if (compact) {
    // Compact horizontal view for cards
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <div
              className={`w-2 h-2 rounded-full ${
                step.status === 'completed'
                  ? 'bg-emerald-500'
                  : step.status === 'current'
                  ? 'bg-primary'
                  : 'bg-slate-300 dark:bg-slate-600'
              }`}
              title={step.label}
            />
            {index < steps.length - 1 && (
              <div
                className={`w-4 h-0.5 ${
                  step.status === 'completed'
                    ? 'bg-emerald-500'
                    : 'bg-slate-300 dark:bg-slate-600'
                }`}
              />
            )}
          </div>
        ))}
      </div>
    );
  }

  // Full horizontal timeline
  return (
    <div className={`flex items-center justify-between ${className}`}>
      {steps.map((step, index) => (
        <div key={step.id} className="flex items-center flex-1">
          {/* Step circle and content */}
          <div className="flex flex-col items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                step.status === 'completed'
                  ? 'bg-emerald-500 text-white'
                  : step.status === 'current'
                  ? 'bg-primary text-primary-foreground ring-4 ring-primary/20'
                  : 'bg-slate-200 dark:bg-slate-700 text-muted-foreground'
              }`}
            >
              {step.status === 'completed' ? (
                <Check className="h-4 w-4" />
              ) : (
                index + 1
              )}
            </div>
            <p
              className={`text-xs mt-2 font-medium text-center ${
                step.status === 'completed'
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : step.status === 'current'
                  ? 'text-primary'
                  : 'text-muted-foreground'
              }`}
            >
              {step.label}
            </p>
            {step.date && (
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {step.date}
              </p>
            )}
          </div>

          {/* Connector line */}
          {index < steps.length - 1 && (
            <div
              className={`flex-1 h-0.5 mx-2 ${
                step.status === 'completed'
                  ? 'bg-emerald-500'
                  : 'bg-slate-200 dark:bg-slate-700'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );
}
